/**
 * Cloud Packet Monitor — End-to-end packet comparison via SFU.
 *
 * Adapted from local-node's packetMonitor.js for cloud frontend.
 * Messages flow: Cloud FE WS → Cloud BE → Tunnel → Local SFU WS
 *
 * 4-hop monitoring:
 *   1. Pipeline RTP sent (ingest → SFU)
 *   2. SFU packets received
 *   3. SFU packets sent to this viewer
 *   4. Browser packets received (RTCPeerConnection.getStats())
 */

import { captureStatsForCamera, CapturedStats } from './cloud-stats-collector';

// ─── Active monitors ─────────────────────────────────────────────
const _activeMonitors = new Map<string, PacketMonitorSession>();
const _listeners = new Set<() => void>();

function _notify(): void {
  _listeners.forEach(fn => { try { fn(); } catch { /* */ } });
}

export function subscribePacketMonitor(listener: () => void): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}

export function getMonitor(cameraId: string): PacketMonitorSession | null {
  return _activeMonitors.get(cameraId) || null;
}

export function getAllMonitors(): Map<string, PacketMonitorSession> {
  return _activeMonitors;
}

export const MONITOR_DURATIONS = [
  { label: '1 min', seconds: 60 },
  { label: '5 min', seconds: 300 },
  { label: '30 min', seconds: 1800 },
];

// ─── Types ───────────────────────────────────────────────────────
export interface FeRunningStats {
  packetsReceived: number;
  bytesReceived: number;
  packetsLost: number;
  framesDecoded: number;
  framesDropped: number;
}

export interface JitterLive {
  current: number;
  avg: number;
  max: number;
  min: number;
  bufferDelay: number;
}

export type MonitorState = 'idle' | 'starting' | 'running' | 'collecting' | 'done' | 'error';

// ─── Session ─────────────────────────────────────────────────────
type GetSignaling = () => { ws: WebSocket | null; pc: RTCPeerConnection | null; sessionId: string | null } | null;

export class PacketMonitorSession {
  cameraId: string;
  durationSec: number;
  state: MonitorState = 'idle';
  error: string | null = null;
  startTime: number | null = null;
  endTime: number | null = null;
  progressPercent = 0;

  feRunning: FeRunningStats = { packetsReceived: 0, bytesReceived: 0, packetsLost: 0, framesDecoded: 0, framesDropped: 0 };
  sfuRunning: any = null;
  jitterLive: JitterLive = { current: 0, avg: 0, max: 0, min: Infinity, bufferDelay: 0 };
  report: any = null;

  private _getSignaling: GetSignaling;
  private _feBaseline: CapturedStats | null = null;
  private _feLatest: CapturedStats | null = null;
  private _feAccumulated: FeRunningStats | null = null;
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  private _progressTimer: ReturnType<typeof setInterval> | null = null;
  private _endTimer: ReturnType<typeof setTimeout> | null = null;

  private jitterSamples: { t: number; jitter_ms: number }[] = [];
  private jitterBufferSamples: { t: number; delay_ms: number }[] = [];
  private pktRateSamples: { t: number; pps: number }[] = [];
  private _prevPktsReceived = 0;
  private _prevPollTime = 0;

  constructor(cameraId: string, getSignaling: GetSignaling, durationSec: number) {
    this.cameraId = cameraId;
    this._getSignaling = getSignaling;
    this.durationSec = durationSec;
  }

  private get pc(): RTCPeerConnection | null { return this._getSignaling?.()?.pc || null; }
  private get ws(): WebSocket | null { return this._getSignaling?.()?.ws || null; }

  async start(): Promise<void> {
    if (this.state !== 'idle') throw new Error(`Cannot start: state is ${this.state}`);

    const pc = this.pc;
    const ws = this.ws;
    if (!pc || pc.connectionState === 'closed') throw new Error('Peer connection not active');
    if (!ws || ws.readyState !== WebSocket.OPEN) throw new Error('Signaling WS not connected');

    this.state = 'starting';
    _notify();

    try {
      this._feBaseline = await captureStatsForCamera(pc, this.cameraId);
      if (!this._feBaseline) throw new Error('No inbound-rtp video stats for this camera');

      ws.send(JSON.stringify({
        type: 'packet_monitor_start',
        cameraId: this.cameraId,
        duration_sec: this.durationSec,
      }));

      this.state = 'running';
      this.startTime = Date.now();
      _notify();

      this._pollTimer = setInterval(() => this._pollFeStats(), 1000);
      this._progressTimer = setInterval(() => {
        if (this.startTime) {
          this.progressPercent = Math.min(100, ((Date.now() - this.startTime) / (this.durationSec * 1000)) * 100);
          _notify();
        }
      }, 500);
      this._endTimer = setTimeout(() => {
        if (this.state === 'running') this._finishFeSide();
      }, (this.durationSec + 15) * 1000);
    } catch (err: any) {
      this.state = 'error';
      this.error = err.message;
      _notify();
      throw err;
    }
  }

  cancel(): void {
    this._cleanup();
    const ws = this.ws;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'packet_monitor_stop', cameraId: this.cameraId }));
    }
    this.state = 'done';
    this.error = 'Cancelled by user';
    _notify();
  }

  handleSFUResult(_data: any): void {
    this._finishFeSide();
  }

  handleProgress(data: any): void {
    if (data.progress_percent != null) this.progressPercent = data.progress_percent;
    if (data.sfu_running) this.sfuRunning = data.sfu_running;
    _notify();
  }

  handleReport(data: any): void {
    this.report = data;
    this.state = 'done';
    this.endTime = Date.now();
    this.progressPercent = 100;
    this._cleanup();
    _notify();
  }

  handleError(message: string): void {
    this.state = 'error';
    this.error = message;
    this._cleanup();
    _notify();
  }

  private async _pollFeStats(): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    const stats = await captureStatsForCamera(pc, this.cameraId);
    if (!stats || !this._feBaseline) return;

    // Handle PC reconnect (counter reset)
    if (stats.packetsReceived < this._feBaseline.packetsReceived) {
      this._feAccumulated = {
        packetsReceived: (this._feAccumulated?.packetsReceived || 0) + (this._feLatest ? this._feLatest.packetsReceived - this._feBaseline.packetsReceived : 0),
        bytesReceived: (this._feAccumulated?.bytesReceived || 0) + (this._feLatest ? this._feLatest.bytesReceived - this._feBaseline.bytesReceived : 0),
        packetsLost: (this._feAccumulated?.packetsLost || 0) + (this._feLatest ? this._feLatest.packetsLost - this._feBaseline.packetsLost : 0),
        framesDecoded: (this._feAccumulated?.framesDecoded || 0) + (this._feLatest ? this._feLatest.framesDecoded - this._feBaseline.framesDecoded : 0),
        framesDropped: (this._feAccumulated?.framesDropped || 0) + (this._feLatest ? this._feLatest.framesDropped - this._feBaseline.framesDropped : 0),
      };
      this._feBaseline = stats;
    }

    this._feLatest = stats;
    const acc = this._feAccumulated || { packetsReceived: 0, bytesReceived: 0, packetsLost: 0, framesDecoded: 0, framesDropped: 0 };
    this.feRunning = {
      packetsReceived: acc.packetsReceived + (stats.packetsReceived - this._feBaseline.packetsReceived),
      bytesReceived: acc.bytesReceived + (stats.bytesReceived - this._feBaseline.bytesReceived),
      packetsLost: acc.packetsLost + Math.max(0, stats.packetsLost - this._feBaseline.packetsLost),
      framesDecoded: acc.framesDecoded + (stats.framesDecoded - this._feBaseline.framesDecoded),
      framesDropped: acc.framesDropped + (stats.framesDropped - this._feBaseline.framesDropped),
    };

    // Jitter tracking
    const now = Date.now();
    const elapsed = this.startTime ? (now - this.startTime) / 1000 : 0;
    const jitterMs = (stats.jitter || 0) * 1000;
    this.jitterSamples.push({ t: elapsed, jitter_ms: jitterMs });
    this.jitterLive.current = jitterMs;
    if (this.jitterSamples.length > 0) {
      this.jitterLive.avg = this.jitterSamples.reduce((a, s) => a + s.jitter_ms, 0) / this.jitterSamples.length;
    }
    if (jitterMs > this.jitterLive.max) this.jitterLive.max = jitterMs;
    if (jitterMs < this.jitterLive.min) this.jitterLive.min = jitterMs;

    // Jitter buffer delay
    if (stats.jitterBufferEmittedCount > 0 && this._feBaseline.jitterBufferEmittedCount != null) {
      const deltaDelay = (stats.jitterBufferDelay || 0) - (this._feBaseline.jitterBufferDelay || 0);
      const deltaEmitted = (stats.jitterBufferEmittedCount || 0) - (this._feBaseline.jitterBufferEmittedCount || 0);
      if (deltaEmitted > 0) {
        const avgDelayMs = (deltaDelay / deltaEmitted) * 1000;
        this.jitterBufferSamples.push({ t: elapsed, delay_ms: avgDelayMs });
        this.jitterLive.bufferDelay = avgDelayMs;
      }
    }

    // Packet rate tracking
    const currentPkts = this.feRunning.packetsReceived;
    if (this._prevPollTime > 0) {
      const dt = (now - this._prevPollTime) / 1000;
      if (dt > 0) this.pktRateSamples.push({ t: elapsed, pps: Math.round((currentPkts - this._prevPktsReceived) / dt) });
    }
    this._prevPollTime = now;
    this._prevPktsReceived = currentPkts;

    _notify();
  }

  private async _finishFeSide(): Promise<void> {
    if (this.state !== 'running') return;
    this.state = 'collecting';
    _notify();

    const pc = this.pc;
    if (!pc) return;
    const finalStats = await captureStatsForCamera(pc, this.cameraId);
    if (finalStats && this._feBaseline) {
      if (finalStats.packetsReceived < this._feBaseline.packetsReceived) {
        this._feAccumulated = {
          packetsReceived: (this._feAccumulated?.packetsReceived || 0) + (this._feLatest ? this._feLatest.packetsReceived - this._feBaseline.packetsReceived : 0),
          bytesReceived: (this._feAccumulated?.bytesReceived || 0) + (this._feLatest ? this._feLatest.bytesReceived - this._feBaseline.bytesReceived : 0),
          packetsLost: (this._feAccumulated?.packetsLost || 0) + (this._feLatest ? this._feLatest.packetsLost - this._feBaseline.packetsLost : 0),
          framesDecoded: (this._feAccumulated?.framesDecoded || 0) + (this._feLatest ? this._feLatest.framesDecoded - this._feBaseline.framesDecoded : 0),
          framesDropped: (this._feAccumulated?.framesDropped || 0) + (this._feLatest ? this._feLatest.framesDropped - this._feBaseline.framesDropped : 0),
        };
        this._feBaseline = finalStats;
      }
      this._feLatest = finalStats;
      const acc = this._feAccumulated || { packetsReceived: 0, bytesReceived: 0, packetsLost: 0, framesDecoded: 0, framesDropped: 0 };
      this.feRunning = {
        packetsReceived: acc.packetsReceived + (finalStats.packetsReceived - this._feBaseline.packetsReceived),
        bytesReceived: acc.bytesReceived + (finalStats.bytesReceived - this._feBaseline.bytesReceived),
        packetsLost: Math.max(0, acc.packetsLost + (finalStats.packetsLost - this._feBaseline.packetsLost)),
        framesDecoded: acc.framesDecoded + (finalStats.framesDecoded - this._feBaseline.framesDecoded),
        framesDropped: acc.framesDropped + (finalStats.framesDropped - this._feBaseline.framesDropped),
      };
    }

    // Compute jitter summary
    const jVals = this.jitterSamples.map(s => s.jitter_ms);
    const jAvg = jVals.length > 0 ? jVals.reduce((a, b) => a + b, 0) / jVals.length : 0;
    const jMax = jVals.length > 0 ? Math.max(...jVals) : 0;
    const sorted = [...jVals].sort((a, b) => a - b);
    const jP95 = sorted.length > 0 ? sorted[Math.floor(sorted.length * 0.95)] : 0;
    const jP99 = sorted.length > 0 ? sorted[Math.floor(sorted.length * 0.99)] : 0;

    const bufVals = this.jitterBufferSamples.map(s => s.delay_ms);
    const bufAvg = bufVals.length > 0 ? bufVals.reduce((a, b) => a + b, 0) / bufVals.length : 0;
    const bufMax = bufVals.length > 0 ? Math.max(...bufVals) : 0;

    const rates = this.pktRateSamples.map(s => s.pps);
    const avgRate = rates.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;
    const stalls = avgRate > 0 ? rates.filter(r => r < avgRate * 0.5).length : 0;

    // Send FE stats to SFU via signaling WS
    const ws = this.ws;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'packet_monitor_fe_stats',
        cameraId: this.cameraId,
        fe_stats: {
          ...this.feRunning,
          jitter_summary: {
            avg_ms: Math.round(jAvg * 100) / 100,
            max_ms: Math.round(jMax * 100) / 100,
            p95_ms: Math.round(jP95 * 100) / 100,
            p99_ms: Math.round(jP99 * 100) / 100,
            samples: jVals.length,
          },
          jitter_buffer: {
            avg_delay_ms: Math.round(bufAvg * 100) / 100,
            max_delay_ms: Math.round(bufMax * 100) / 100,
          },
          arrival_rate: {
            avg_pps: Math.round(avgRate),
            stalls,
          },
        },
      }));
    }

    this._stopPolling();
  }

  private _stopPolling(): void {
    if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
    if (this._progressTimer) { clearInterval(this._progressTimer); this._progressTimer = null; }
  }

  private _cleanup(): void {
    this._stopPolling();
    if (this._endTimer) { clearTimeout(this._endTimer); this._endTimer = null; }
  }
}

// ─── Public API ──────────────────────────────────────────────────
export async function startPacketMonitor(cameraId: string, getSignaling: GetSignaling, durationSec: number): Promise<PacketMonitorSession> {
  const existing = _activeMonitors.get(cameraId);
  if (existing && (existing.state === 'running' || existing.state === 'starting')) {
    existing.cancel();
  }

  const session = new PacketMonitorSession(cameraId, getSignaling, durationSec);
  _activeMonitors.set(cameraId, session);
  _notify();

  await session.start();
  return session;
}

export function cancelPacketMonitor(cameraId: string): void {
  const session = _activeMonitors.get(cameraId);
  if (session) session.cancel();
}

export function clearPacketMonitor(cameraId: string): void {
  _activeMonitors.delete(cameraId);
  _notify();
}

export function handlePacketMonitorMessage(msg: any): boolean {
  const { type, camera_id } = msg;
  if (!type?.startsWith('packet_monitor_')) return false;

  const session = _activeMonitors.get(camera_id);
  if (!session) {
    console.warn(`[PacketMonitor] Got ${type} for unknown camera ${camera_id}`);
    return true;
  }

  switch (type) {
    case 'packet_monitor_result':
      session.handleSFUResult(msg.sfu_stats);
      break;
    case 'packet_monitor_progress':
      session.handleProgress(msg);
      break;
    case 'packet_monitor_report':
      session.handleReport(msg.report);
      break;
    case 'packet_monitor_error':
      session.handleError(msg.message || 'Unknown error');
      break;
    default:
      console.warn(`[PacketMonitor] Unknown type: ${type}`);
  }
  return true;
}
